import React, { useState, useEffect } from 'react';
import {
    AppBar,
    Box,
    Toolbar,
    IconButton,
    Typography,
    Menu,
    MenuItem,
    Container,
    Avatar,
    Button,
    Tooltip,
    Divider,
    ListItemIcon,
    useTheme,
    useMediaQuery,
    styled
} from '@mui/material';
import {
    Menu as MenuIcon,
    Person as PersonIcon,
    Favorite as FavoriteIcon,
    Message as MessageIcon,
    Settings as SettingsIcon,
    Logout as LogoutIcon,
    Home as HomeIcon,
    Search as SearchIcon,
    Add as AddIcon,
    ContactSupport as ContactSupportIcon,
    Help as HelpIcon,
    Security as SecurityIcon,
    Gavel as GavelIcon,
    ListAlt as ListAltIcon,
    Group as GroupIcon,
} from '@mui/icons-material';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { UserAvatar } from '../common/UserAvatar';
import MobileNav from './MobileNav';

const TopBanner = styled(Box)(({ theme }) => ({
    width: '100%',
    background: '#fff',
    padding: theme.spacing(2, 0),
    textAlign: 'center',
    position: 'relative',
    borderBottom: '1px solid rgba(0,0,0,0.04)',
    boxShadow: '0 2px 12px rgba(0,0,0,0.03)',
    // モバイル表示時のスタイル
    [theme.breakpoints.down('md')]: {
        padding: theme.spacing(3, 0),
        '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: `
            radial-gradient(#3498db 1px, transparent 1px),
            radial-gradient(#5dade2 1px, transparent 1px)
        `,
            backgroundSize: '40px 40px, 30px 30px',
            backgroundPosition: '0 0, 20px 20px',
            opacity: 0.03,
            pointerEvents: 'none'
        },
        '&::after': {
            content: '""',
            position: 'absolute',
            left: 0,
            bottom: 0,
            width: '100%',
            height: '3px',
            background: 'linear-gradient(90deg, #3498db, #5dade2)',
            opacity: 0.8
        }
    },
    // PC表示時のスタイル
    [theme.breakpoints.up('md')]: {
        background: 'linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)',
        padding: theme.spacing(1.5, 0),
        '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '2px',
            background: 'linear-gradient(90deg, #3498db, #5dade2, #4ecdc4)',
            opacity: 0.8
        }
    }
}));

const MainTitle = styled(Typography)(({ theme }) => ({
    fontSize: '2.8rem',
    fontWeight: 800,
    fontFamily: "'Noto Sans JP', sans-serif",
    background: 'linear-gradient(135deg, #3498db 0%, #5dade2 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    marginBottom: theme.spacing(1),
    letterSpacing: '0.05em',
    position: 'relative',
    display: 'inline-block',
    padding: '0 1rem',
    transition: 'all 0.3s ease',
    textShadow: '0 5px 15px rgba(52, 152, 219, 0.1)',
    '&::after': {
        content: '""',
        position: 'absolute',
        bottom: 0,
        left: '25%',
        width: '50%',
        height: '2px',
        background: 'linear-gradient(90deg, transparent, #5dade2, transparent)',
        borderRadius: '2px',
        opacity: 0.8,
        transition: 'all 0.3s ease'
    },
    '&:hover': {
        transform: 'scale(1.02) translateY(-2px)',
        cursor: 'pointer',
        opacity: 0.9,
        textShadow: '0 8px 20px rgba(52, 152, 219, 0.2)',
        '&::after': {
            width: '70%',
            left: '15%',
            opacity: 1
        }
    },
    [theme.breakpoints.down('md')]: {
        fontSize: '2.2rem',
        padding: '0 0.5rem'
    },
    // PC表示時のスタイル
    [theme.breakpoints.up('md')]: {
        fontSize: '2rem',
        marginBottom: 0,
        padding: 0,
        '&::after': {
            display: 'none'
        },
        '&:hover': {
            transform: 'scale(1.01)',
            textShadow: '0 4px 12px rgba(52, 152, 219, 0.15)'
        }
    }
}));

const SubTitle = styled(Typography)(({ theme }) => ({
    fontSize: '1.1rem',
    fontFamily: "'Noto Sans JP', sans-serif",
    color: '#475569',
    lineHeight: 1.6,
    letterSpacing: '0.05em',
    fontWeight: 500,
    marginTop: theme.spacing(0.5),
    maxWidth: '100%',
    margin: '0 auto',
    whiteSpace: 'nowrap',
    [theme.breakpoints.down('md')]: {
        fontSize: '0.95rem',
        letterSpacing: '0.02em'
    },
    // PC表示時のスタイル
    [theme.breakpoints.up('md')]: {
        fontSize: '0.9rem',
        marginTop: theme.spacing(0.2),
        color: '#64748b',
        fontWeight: 400
    }
}));

// PC表示時のヘッダーコンテナ
const DesktopHeaderContainer = styled(Box)(({ theme }) => ({
    [theme.breakpoints.up('md')]: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        maxWidth: '1200px',
        margin: '0 auto',
        padding: '0 2rem',
        position: 'relative'
    },
    [theme.breakpoints.down('md')]: {
        display: 'block',
        textAlign: 'center'
    }
}));

function Navbar() {
    const [anchorElNav, setAnchorElNav] = useState(null);
    const [anchorElUser, setAnchorElUser] = useState(null);
    const [anchorElAccount, setAnchorElAccount] = useState(null);
    const { currentUser, userProfile, logout } = useAuth();
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const location = useLocation();

    const handleOpenNavMenu = (event) => {
        setAnchorElNav(event.currentTarget);
    };

    const handleOpenUserMenu = (event) => {
        setAnchorElUser(event.currentTarget);
    };

    const handleCloseNavMenu = () => {
        setAnchorElNav(null);
    };

    const handleCloseUserMenu = () => {
        setAnchorElUser(null);
    };

    const handleCloseAccountMenu = () => {
        setAnchorElAccount(null);
    };

    // アカウントボタンのマウスホバーでメニューを開く
    const handleAccountMouseEnter = (event) => {
        setAnchorElAccount(event.currentTarget.querySelector('button'));
    };

    // アカウントコンテナからマウスが離れた時の処理
    const handleAccountMouseLeave = () => {
        setAnchorElAccount(null);
    };

    // プロフィールアイコンのマウスホバーでメニューを開く
    const handleUserMouseEnter = (event) => {
        setAnchorElUser(event.currentTarget.querySelector('button'));
    };

    // プロフィールアイコンコンテナからマウスが離れた時の処理
    const handleUserMouseLeave = () => {
        setAnchorElUser(null);
    };

    const handleLogout = async () => {
        try {
            await logout();
            handleCloseUserMenu();
            navigate('/');
        } catch (error) {
            console.error('ログアウトエラー:', error);
        }
    };

    const menuItems = [
        { text: '物件', icon: <SearchIcon />, path: '/room-search' },
        { text: 'ルームメイト', icon: <GroupIcon />, path: '/renters' },
        { text: 'メッセージ', icon: <MessageIcon />, path: '/messages' },
        { text: 'メニュー', icon: <PersonIcon />, path: '/profile' },
    ];

    const userMenuItems = [
        { text: 'プロフィール', icon: <PersonIcon />, path: '/profile' },
        { text: 'お気に入り', icon: <FavoriteIcon />, path: '/favorites' },
        { text: '物件を掲載', icon: <AddIcon />, path: '/create-listing' },
        { text: '登録した物件', icon: <ListAltIcon />, path: '/my-rooms' },
        { text: 'メッセージ', icon: <MessageIcon />, path: '/messages' },
        { text: '設定', icon: <SettingsIcon />, path: '/settings' },
    ];

    const helpMenuItems = [
        { text: 'お問い合わせ', icon: <ContactSupportIcon />, path: '/contact' },
        { text: 'ヘルプセンター', icon: <HelpIcon />, path: '/help' },
        { text: 'ブログ', icon: <ListAltIcon />, path: '/blog' },
        { text: 'プライバシーポリシー', icon: <SecurityIcon />, path: '/privacy' },
        { text: '利用規約', icon: <GavelIcon />, path: '/terms' },
    ];

    // 未ログイン時のアカウントメニュー項目（スマホ版を参考）
    const accountMenuItems = [
        { text: 'ログイン', icon: <PersonIcon />, path: '/login' },
        { text: '新規登録', icon: <AddIcon />, path: '/register' },
    ];

    const DEFAULT_AVATAR = process.env.PUBLIC_URL + '/images/default-avatar.jpg';

    // 現在のパスが指定したパスに一致するかをチェックする関数
    const isPathActive = (path) => {
        // ホームページの場合は物件検索を選択状態にする
        if (location.pathname === '/' && path === '/room-search') {
            return true;
        }

        // 正確なパス一致
        if (location.pathname === path) {
            return true;
        }

        // パスの前方一致（サブページの場合）
        if (path !== '/' && location.pathname.startsWith(path)) {
            return true;
        }

        return false;
    };

    return (
        <>
            <TopBanner>
                <DesktopHeaderContainer>
                    {/* PC表示時の左側スペーサー */}
                    <Box sx={{
                        display: { xs: 'none', md: 'block' },
                        width: '200px'
                    }} />

                    {/* ロゴセクション（中央） */}
                    <Box sx={{
                        position: 'relative',
                        display: { xs: 'block', md: 'flex' },
                        alignItems: 'center',
                        textAlign: 'center',
                        flex: { md: '1' },
                        justifyContent: { md: 'center' }
                    }}>
                        {/* モバイル表示時の装飾要素 */}
                        <Box
                            sx={{
                                position: 'absolute',
                                top: -10,
                                left: -30,
                                width: 40,
                                height: 40,
                                borderRadius: '50%',
                                background: 'rgba(52, 152, 219, 0.1)',
                                zIndex: 0,
                                display: { xs: 'block', md: 'none' }
                            }}
                        />
                        <Box
                            sx={{
                                position: 'absolute',
                                bottom: 0,
                                right: -20,
                                width: 25,
                                height: 25,
                                borderRadius: '50%',
                                background: 'rgba(93, 173, 226, 0.15)',
                                zIndex: 0,
                                display: { xs: 'block', md: 'none' }
                            }}
                        />

                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            <MainTitle
                                variant="h1"
                                onClick={() => navigate('/')}
                                sx={{
                                    cursor: 'pointer',
                                    position: 'relative',
                                    zIndex: 1,
                                    '&:hover': {
                                        opacity: 0.9
                                    }
                                }}
                            >
                                ROOM SHARE MATCHING
                            </MainTitle>
                            <SubTitle variant="subtitle1">
                                ルームシェア相手のマッチングプラットフォーム
                            </SubTitle>
                        </Box>
                    </Box>

                    {/* PC表示時のクイックアクション（右側） */}
                    <Box sx={{
                        display: { xs: 'none', md: 'flex' },
                        alignItems: 'center',
                        gap: 2,
                        width: '200px',
                        justifyContent: 'flex-end'
                    }}>
                        {currentUser ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={() => navigate('/create-listing')}
                                    sx={{
                                        borderColor: '#3498db',
                                        color: '#3498db',
                                        '&:hover': {
                                            backgroundColor: 'rgba(52, 152, 219, 0.08)',
                                            borderColor: '#2980b9'
                                        }
                                    }}
                                    startIcon={<AddIcon />}
                                >
                                    物件掲載
                                </Button>
                                <Box
                                    sx={{ position: 'relative' }}
                                    onMouseEnter={handleUserMouseEnter}
                                    onMouseLeave={handleUserMouseLeave}
                                >
                                    <Tooltip title="アカウントメニュー">
                                        <IconButton
                                            onClick={handleOpenUserMenu}
                                            sx={{
                                                p: 0,
                                                transition: 'transform 0.2s ease',
                                                '&:hover': {
                                                    transform: 'scale(1.05)'
                                                }
                                            }}
                                        >
                                            <Avatar
                                                src={userProfile?.profile?.profileImage || '/images/default-avatar.jpg'}
                                                alt={userProfile?.profile?.nickname || 'ユーザー'}
                                                sx={{
                                                    width: 36,
                                                    height: 36,
                                                    border: '2px solid #fff',
                                                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                                                }}
                                            />
                                        </IconButton>
                                    </Tooltip>

                                    {/* プロフィールアイコンのユーザーメニュー */}
                                    <Menu
                                        sx={{
                                            '& .MuiPaper-root': {
                                                borderRadius: 3,
                                                boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                                                minWidth: 220,
                                                border: '1px solid rgba(0,0,0,0.05)',
                                                mt: 1
                                            }
                                        }}
                                        anchorEl={anchorElUser}
                                        anchorOrigin={{
                                            vertical: 'bottom',
                                            horizontal: 'right',
                                        }}
                                        transformOrigin={{
                                            vertical: 'top',
                                            horizontal: 'right',
                                        }}
                                        open={Boolean(anchorElUser)}
                                        onClose={handleCloseUserMenu}
                                        disableAutoFocus
                                        disableEnforceFocus
                                        disableRestoreFocus
                                        MenuListProps={{
                                            onMouseLeave: handleUserMouseLeave
                                        }}
                                    >
                                        {userMenuItems.map((item) => (
                                            <MenuItem
                                                key={item.text}
                                                onClick={() => {
                                                    handleCloseUserMenu();
                                                    navigate(item.path);
                                                }}
                                                sx={{
                                                    py: 1.5,
                                                    px: 2,
                                                    transition: 'all 0.2s ease',
                                                    '&:hover': {
                                                        backgroundColor: 'rgba(52, 152, 219, 0.08)',
                                                        transform: 'translateX(4px)'
                                                    }
                                                }}
                                            >
                                                <ListItemIcon sx={{
                                                    color: '#3498db',
                                                    minWidth: 40
                                                }}>
                                                    {item.icon}
                                                </ListItemIcon>
                                                <Typography
                                                    sx={{
                                                        fontWeight: 500,
                                                        color: '#475569'
                                                    }}
                                                >
                                                    {item.text}
                                                </Typography>
                                            </MenuItem>
                                        ))}
                                        <Divider sx={{ my: 1 }} />
                                        {helpMenuItems.map((item) => (
                                            <MenuItem
                                                key={item.text}
                                                onClick={() => {
                                                    handleCloseUserMenu();
                                                    navigate(item.path);
                                                }}
                                                sx={{
                                                    py: 1.5,
                                                    px: 2,
                                                    transition: 'all 0.2s ease',
                                                    '&:hover': {
                                                        backgroundColor: 'rgba(52, 152, 219, 0.08)',
                                                        transform: 'translateX(4px)'
                                                    }
                                                }}
                                            >
                                                <ListItemIcon sx={{
                                                    color: '#3498db',
                                                    minWidth: 40
                                                }}>
                                                    {item.icon}
                                                </ListItemIcon>
                                                <Typography
                                                    sx={{
                                                        fontWeight: 500,
                                                        color: '#475569'
                                                    }}
                                                >
                                                    {item.text}
                                                </Typography>
                                            </MenuItem>
                                        ))}
                                        <Divider sx={{ my: 1 }} />
                                        <MenuItem
                                            onClick={() => {
                                                handleCloseUserMenu();
                                                handleLogout();
                                            }}
                                            sx={{
                                                py: 1.5,
                                                px: 2,
                                                transition: 'all 0.2s ease',
                                                '&:hover': {
                                                    backgroundColor: 'rgba(231, 76, 60, 0.08)',
                                                    transform: 'translateX(4px)'
                                                }
                                            }}
                                        >
                                            <ListItemIcon sx={{
                                                color: '#e74c3c',
                                                minWidth: 40
                                            }}>
                                                <LogoutIcon />
                                            </ListItemIcon>
                                            <Typography
                                                sx={{
                                                    fontWeight: 500,
                                                    color: '#e74c3c'
                                                }}
                                            >
                                                ログアウト
                                            </Typography>
                                        </MenuItem>
                                    </Menu>
                                </Box>
                            </Box>
                        ) : (
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <Button
                                    component={Link}
                                    to="/login"
                                    variant="outlined"
                                    size="small"
                                    sx={{
                                        borderColor: '#3498db',
                                        color: '#3498db',
                                        '&:hover': {
                                            backgroundColor: 'rgba(52, 152, 219, 0.08)',
                                            borderColor: '#2980b9'
                                        }
                                    }}
                                >
                                    ログイン
                                </Button>
                                <Button
                                    component={Link}
                                    to="/register"
                                    variant="contained"
                                    size="small"
                                    sx={{
                                        background: 'linear-gradient(135deg, #3498db 0%, #5dade2 100%)',
                                        '&:hover': {
                                            background: 'linear-gradient(135deg, #2980b9 0%, #3498db 100%)',
                                        }
                                    }}
                                >
                                    新規登録
                                </Button>
                            </Box>
                        )}
                    </Box>
                </DesktopHeaderContainer>
            </TopBanner>
            <MobileNav />
            <AppBar
                position="static"
                elevation={0}
                sx={{
                    backgroundColor: '#fff',
                    borderBottom: '1px solid rgba(0,0,0,0.04)',
                    display: { xs: 'none', md: 'block' },
                    transition: 'all 0.3s ease',
                    boxShadow: '0 2px 12px rgba(0,0,0,0.03)'
                }}
            >
                <Container maxWidth="xl">
                    <Toolbar disableGutters sx={{ justifyContent: 'center' }}>
                        {/* メニュー項目（PC） */}
                        <Box sx={{ display: 'flex', gap: 4 }}>
                            {menuItems.map((item) => {
                                // アカウントボタンの場合は特別な処理（未ログイン時）
                                if (item.text === 'メニュー' && !currentUser) {
                                    return (
                                        <Box
                                            key={item.text}
                                            sx={{ position: 'relative' }}
                                            onMouseEnter={handleAccountMouseEnter}
                                            onMouseLeave={handleAccountMouseLeave}
                                        >
                                            <Button
                                                sx={{
                                                    my: 2,
                                                    color: isPathActive(item.path) ? '#3498db' : '#475569',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    fontWeight: isPathActive(item.path) ? 700 : 500,
                                                    fontSize: '1rem',
                                                    transition: 'all 0.2s ease',
                                                    position: 'relative',
                                                    px: 3,
                                                    py: 1.5,
                                                    borderRadius: 2,
                                                    cursor: 'default',
                                                    '&::after': {
                                                        content: '""',
                                                        position: 'absolute',
                                                        bottom: -8,
                                                        left: '50%',
                                                        width: isPathActive(item.path) ? '80%' : '0%',
                                                        height: '3px',
                                                        backgroundColor: '#3498db',
                                                        transition: 'all 0.3s ease',
                                                        transform: 'translateX(-50%)',
                                                        borderRadius: '2px'
                                                    },
                                                    '&:hover': {
                                                        backgroundColor: 'rgba(52, 152, 219, 0.08)',
                                                        color: '#3498db',
                                                        '&::after': {
                                                            width: '80%'
                                                        }
                                                    },
                                                    '& .MuiSvgIcon-root': {
                                                        transition: 'transform 0.2s ease',
                                                        mr: 1,
                                                        color: isPathActive(item.path) ? '#3498db' : 'inherit'
                                                    }
                                                }}
                                                startIcon={item.icon}
                                            >
                                                {item.text}
                                            </Button>

                                            {/* アカウントメニュー（未ログイン時用） */}
                                            <Menu
                                                sx={{
                                                    '& .MuiPaper-root': {
                                                        borderRadius: 3,
                                                        boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                                                        minWidth: 220,
                                                        border: '1px solid rgba(0,0,0,0.05)',
                                                        mt: 1
                                                    }
                                                }}
                                                anchorEl={anchorElAccount}
                                                anchorOrigin={{
                                                    vertical: 'bottom',
                                                    horizontal: 'center',
                                                }}
                                                transformOrigin={{
                                                    vertical: 'top',
                                                    horizontal: 'center',
                                                }}
                                                open={Boolean(anchorElAccount)}
                                                onClose={handleCloseAccountMenu}
                                                disableAutoFocus
                                                disableEnforceFocus
                                                disableRestoreFocus
                                                MenuListProps={{
                                                    onMouseLeave: handleAccountMouseLeave
                                                }}
                                            >
                                                {accountMenuItems.map((menuItem) => (
                                                    <MenuItem
                                                        key={menuItem.text}
                                                        onClick={() => {
                                                            handleCloseAccountMenu();
                                                            navigate(menuItem.path);
                                                        }}
                                                        sx={{
                                                            py: 1.5,
                                                            px: 2,
                                                            transition: 'all 0.2s ease',
                                                            '&:hover': {
                                                                backgroundColor: 'rgba(52, 152, 219, 0.08)',
                                                                transform: 'translateX(4px)'
                                                            }
                                                        }}
                                                    >
                                                        <ListItemIcon sx={{
                                                            color: '#3498db',
                                                            minWidth: 40
                                                        }}>
                                                            {menuItem.icon}
                                                        </ListItemIcon>
                                                        <Typography
                                                            sx={{
                                                                fontWeight: 500,
                                                                color: '#475569'
                                                            }}
                                                        >
                                                            {menuItem.text}
                                                        </Typography>
                                                    </MenuItem>
                                                ))}
                                                <Divider sx={{ my: 1 }} />
                                                {helpMenuItems.map((menuItem) => (
                                                    <MenuItem
                                                        key={menuItem.text}
                                                        onClick={() => {
                                                            handleCloseAccountMenu();
                                                            navigate(menuItem.path);
                                                        }}
                                                        sx={{
                                                            py: 1.5,
                                                            px: 2,
                                                            transition: 'all 0.2s ease',
                                                            '&:hover': {
                                                                backgroundColor: 'rgba(52, 152, 219, 0.08)',
                                                                transform: 'translateX(4px)'
                                                            }
                                                        }}
                                                    >
                                                        <ListItemIcon sx={{
                                                            color: '#3498db',
                                                            minWidth: 40
                                                        }}>
                                                            {menuItem.icon}
                                                        </ListItemIcon>
                                                        <Typography
                                                            sx={{
                                                                fontWeight: 500,
                                                                color: '#475569'
                                                            }}
                                                        >
                                                            {menuItem.text}
                                                        </Typography>
                                                    </MenuItem>
                                                ))}
                                            </Menu>
                                        </Box>
                                    );
                                }

                                // アカウントボタンの場合は特別な処理（ログイン時）
                                if (item.text === 'メニュー' && currentUser) {
                                    return (
                                        <Box
                                            key={item.text}
                                            sx={{ position: 'relative' }}
                                            onMouseEnter={handleAccountMouseEnter}
                                            onMouseLeave={handleAccountMouseLeave}
                                        >
                                            <Button
                                                sx={{
                                                    my: 2,
                                                    color: isPathActive(item.path) ? '#3498db' : '#475569',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    fontWeight: isPathActive(item.path) ? 700 : 500,
                                                    fontSize: '1rem',
                                                    transition: 'all 0.2s ease',
                                                    position: 'relative',
                                                    px: 3,
                                                    py: 1.5,
                                                    borderRadius: 2,
                                                    cursor: 'default',
                                                    '&::after': {
                                                        content: '""',
                                                        position: 'absolute',
                                                        bottom: -8,
                                                        left: '50%',
                                                        width: isPathActive(item.path) ? '80%' : '0%',
                                                        height: '3px',
                                                        backgroundColor: '#3498db',
                                                        transition: 'all 0.3s ease',
                                                        transform: 'translateX(-50%)',
                                                        borderRadius: '2px'
                                                    },
                                                    '&:hover': {
                                                        backgroundColor: 'rgba(52, 152, 219, 0.08)',
                                                        color: '#3498db',
                                                        '&::after': {
                                                            width: '80%'
                                                        }
                                                    },
                                                    '& .MuiSvgIcon-root': {
                                                        transition: 'transform 0.2s ease',
                                                        mr: 1,
                                                        color: isPathActive(item.path) ? '#3498db' : 'inherit'
                                                    }
                                                }}
                                                startIcon={item.icon}
                                            >
                                                {item.text}
                                            </Button>

                                            {/* アカウントメニュー（ログイン時用） */}
                                            <Menu
                                                sx={{
                                                    '& .MuiPaper-root': {
                                                        borderRadius: 3,
                                                        boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                                                        minWidth: 220,
                                                        border: '1px solid rgba(0,0,0,0.05)',
                                                        mt: 1
                                                    }
                                                }}
                                                anchorEl={anchorElAccount}
                                                anchorOrigin={{
                                                    vertical: 'bottom',
                                                    horizontal: 'center',
                                                }}
                                                transformOrigin={{
                                                    vertical: 'top',
                                                    horizontal: 'center',
                                                }}
                                                open={Boolean(anchorElAccount)}
                                                onClose={handleCloseAccountMenu}
                                                disableAutoFocus
                                                disableEnforceFocus
                                                disableRestoreFocus
                                                MenuListProps={{
                                                    onMouseLeave: handleAccountMouseLeave
                                                }}
                                            >
                                                {userMenuItems.map((menuItem) => (
                                                    <MenuItem
                                                        key={menuItem.text}
                                                        onClick={() => {
                                                            handleCloseAccountMenu();
                                                            navigate(menuItem.path);
                                                        }}
                                                        sx={{
                                                            py: 1.5,
                                                            px: 2,
                                                            transition: 'all 0.2s ease',
                                                            '&:hover': {
                                                                backgroundColor: 'rgba(52, 152, 219, 0.08)',
                                                                transform: 'translateX(4px)'
                                                            }
                                                        }}
                                                    >
                                                        <ListItemIcon sx={{
                                                            color: '#3498db',
                                                            minWidth: 40
                                                        }}>
                                                            {menuItem.icon}
                                                        </ListItemIcon>
                                                        <Typography
                                                            sx={{
                                                                fontWeight: 500,
                                                                color: '#475569'
                                                            }}
                                                        >
                                                            {menuItem.text}
                                                        </Typography>
                                                    </MenuItem>
                                                ))}
                                                <Divider sx={{ my: 1 }} />
                                                {helpMenuItems.map((menuItem) => (
                                                    <MenuItem
                                                        key={menuItem.text}
                                                        onClick={() => {
                                                            handleCloseAccountMenu();
                                                            navigate(menuItem.path);
                                                        }}
                                                        sx={{
                                                            py: 1.5,
                                                            px: 2,
                                                            transition: 'all 0.2s ease',
                                                            '&:hover': {
                                                                backgroundColor: 'rgba(52, 152, 219, 0.08)',
                                                                transform: 'translateX(4px)'
                                                            }
                                                        }}
                                                    >
                                                        <ListItemIcon sx={{
                                                            color: '#3498db',
                                                            minWidth: 40
                                                        }}>
                                                            {menuItem.icon}
                                                        </ListItemIcon>
                                                        <Typography
                                                            sx={{
                                                                fontWeight: 500,
                                                                color: '#475569'
                                                            }}
                                                        >
                                                            {menuItem.text}
                                                        </Typography>
                                                    </MenuItem>
                                                ))}
                                                <Divider sx={{ my: 1 }} />
                                                <MenuItem
                                                    onClick={() => {
                                                        handleCloseAccountMenu();
                                                        handleLogout();
                                                    }}
                                                    sx={{
                                                        py: 1.5,
                                                        px: 2,
                                                        transition: 'all 0.2s ease',
                                                        '&:hover': {
                                                            backgroundColor: 'rgba(231, 76, 60, 0.08)',
                                                            transform: 'translateX(4px)'
                                                        }
                                                    }}
                                                >
                                                    <ListItemIcon sx={{
                                                        color: '#e74c3c',
                                                        minWidth: 40
                                                    }}>
                                                        <LogoutIcon />
                                                    </ListItemIcon>
                                                    <Typography
                                                        sx={{
                                                            fontWeight: 500,
                                                            color: '#e74c3c'
                                                        }}
                                                    >
                                                        ログアウト
                                                    </Typography>
                                                </MenuItem>
                                            </Menu>
                                        </Box>
                                    );
                                }

                                // 通常のボタン
                                return (
                                    <Button
                                        key={item.text}
                                        onClick={() => navigate(item.path)}
                                        sx={{
                                            my: 2,
                                            color: isPathActive(item.path) ? '#3498db' : '#475569',
                                            display: 'flex',
                                            alignItems: 'center',
                                            fontWeight: isPathActive(item.path) ? 700 : 500,
                                            fontSize: '1rem',
                                            transition: 'all 0.2s ease',
                                            position: 'relative',
                                            px: 3,
                                            py: 1.5,
                                            borderRadius: 2,
                                            cursor: 'pointer',
                                            '&::after': {
                                                content: '""',
                                                position: 'absolute',
                                                bottom: -8,
                                                left: '50%',
                                                width: isPathActive(item.path) ? '80%' : '0%',
                                                height: '3px',
                                                backgroundColor: '#3498db',
                                                transition: 'all 0.3s ease',
                                                transform: 'translateX(-50%)',
                                                borderRadius: '2px'
                                            },
                                            '&:hover': {
                                                backgroundColor: 'rgba(52, 152, 219, 0.08)',
                                                color: '#3498db',
                                                transform: 'translateY(-2px)',
                                                '&::after': {
                                                    width: '80%'
                                                }
                                            },
                                            '& .MuiSvgIcon-root': {
                                                transition: 'transform 0.2s ease',
                                                mr: 1,
                                                color: isPathActive(item.path) ? '#3498db' : 'inherit'
                                            },
                                            '&:hover .MuiSvgIcon-root': {
                                                transform: 'scale(1.1)'
                                            }
                                        }}
                                        startIcon={item.icon}
                                    >
                                        {item.text}
                                    </Button>
                                );
                            })}
                        </Box>
                    </Toolbar>
                </Container>
            </AppBar>
        </>
    );
}

export default Navbar; 